import { useState, FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'

interface RegisterResponse {
  message: string
  userId: string
}

interface RegisterError {
  statusCode?: number
  error: string
}

interface PasswordRequirement {
  regex: RegExp
  text: string
  met: boolean
}

export function Register() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  })
  const [errors, setErrors] = useState<{
    name?: string
    email?: string
    password?: string
    confirmPassword?: string
    general?: string
  }>({})
  const [isLoading, setIsLoading] = useState(false)
  const [registrationSuccess, setRegistrationSuccess] = useState(false)

  // Password requirements
  const [passwordRequirements, setPasswordRequirements] = useState<PasswordRequirement[]>([
    { regex: /.{8,}/, text: 'At least 8 characters', met: false },
    { regex: /[a-z]/, text: 'One lowercase letter', met: false },
    { regex: /[A-Z]/, text: 'One uppercase letter', met: false },
    { regex: /\d/, text: 'One number', met: false },
    { regex: /[@$!%*?&]/, text: 'One special character (@$!%*?&)', met: false }
  ])

  const validateForm = () => {
    const newErrors: typeof errors = {}

    // Name validation
    if (!formData.name) {
      newErrors.name = 'Name is required'
    } else if (formData.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters'
    }

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else {
      // Check all password requirements
      const allRequirementsMet = passwordRequirements.every(req => req.met)
      if (!allRequirementsMet) {
        newErrors.password = 'Password does not meet all requirements'
      }
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const checkPasswordRequirements = (password: string) => {
    setPasswordRequirements(prev =>
      prev.map(req => ({
        ...req,
        met: req.regex.test(password)
      }))
    )
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    setErrors({})

    try {
      const response = await fetch('http://localhost:3000/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password
        })
      })

      const data: RegisterResponse | RegisterError = await response.json()

      if (!response.ok) {
        const error = data as RegisterError

        // Handle specific error cases
        if (response.status === 409) {
          setErrors({ general: error.error || 'An account with this email already exists' })
        } else if (response.status === 400) {
          setErrors({ general: error.error || 'Invalid input. Please check your information.' })
        } else {
          setErrors({ general: error.error || 'Registration failed. Please try again.' })
        }
        return
      }

      // Show success message
      setRegistrationSuccess(true)

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', {
          state: {
            message: 'Registration successful! Please check your email to verify your account before logging in.',
            type: 'success'
          }
        })
      }, 3000)
    } catch (error) {
      setErrors({
        general: 'Network error. Please check your connection and try again.'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setFormData((prev) => ({
      ...prev,
      [field]: newValue
    }))

    // Check password requirements when password changes
    if (field === 'password') {
      checkPasswordRequirements(newValue)
    }

    // Clear field-specific error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined
      }))
    }
  }

  // Show success state
  if (registrationSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-5">
        <div className="fixed top-4 right-4 z-50">
          {/* ThemeToggle component will be added here via App.tsx */}
        </div>

        <div className="w-full max-w-md">
          <div className="bg-card p-8 rounded-lg shadow-lg border border-border text-center">
            <div className="mb-6">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-green-600 dark:text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Registration Successful!
              </h1>
              <p className="text-muted-foreground">
                We've sent a verification email to <strong>{formData.email}</strong>.
                Please check your inbox and click the verification link to activate your account.
              </p>
            </div>

            <div className="space-y-3 text-sm text-muted-foreground">
              <p>✓ Check your spam folder if you don't see the email within a few minutes.</p>
              <p>✓ The verification link will expire in 24 hours.</p>
              <p className="pt-2">
                Redirecting to login page<span className="animate-pulse">...</span>
              </p>
            </div>

            <button
              onClick={() => navigate('/login')}
              className="mt-6 w-full px-4 py-3 bg-primary text-primary-foreground rounded-md font-medium transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              Go to Login Now
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-5">
      {/* Theme Toggle in top-right corner */}
      <div className="fixed top-4 right-4 z-50">
        {/* ThemeToggle component will be added here via App.tsx */}
      </div>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2 text-foreground">
            Create Account
          </h1>
          <p className="text-muted-foreground">
            Join securesight.ai today
          </p>
        </div>

        <div className="bg-card p-8 rounded-lg shadow-lg border border-border">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* General Error Message */}
            {errors.general && (
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                <p className="text-sm text-destructive font-medium">{errors.general}</p>
              </div>
            )}

            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                Full Name
              </label>
              <input
                id="name"
                type="text"
                autoComplete="name"
                value={formData.name}
                onChange={handleInputChange('name')}
                className={`w-full px-4 py-2 bg-background border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
                  errors.name ? 'border-destructive' : 'border-border'
                }`}
                placeholder="John Doe"
                disabled={isLoading}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                className={`w-full px-4 py-2 bg-background border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
                  errors.email ? 'border-destructive' : 'border-border'
                }`}
                placeholder="you@example.com"
                disabled={isLoading}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-destructive">{errors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleInputChange('password')}
                className={`w-full px-4 py-2 bg-background border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
                  errors.password ? 'border-destructive' : 'border-border'
                }`}
                placeholder="••••••••"
                disabled={isLoading}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-destructive">{errors.password}</p>
              )}

              {/* Password Requirements */}
              {formData.password && (
                <div className="mt-2 p-3 bg-muted/50 rounded-md">
                  <p className="text-xs font-medium text-foreground mb-2">Password must contain:</p>
                  <ul className="space-y-1">
                    {passwordRequirements.map((req, index) => (
                      <li
                        key={index}
                        className={`text-xs flex items-center gap-2 ${
                          req.met ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'
                        }`}
                      >
                        <span className={req.met ? '✓' : '○'}>
                          {req.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-2">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                className={`w-full px-4 py-2 bg-background border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
                  errors.confirmPassword ? 'border-destructive' : 'border-border'
                }`}
                placeholder="••••••••"
                disabled={isLoading}
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-destructive">{errors.confirmPassword}</p>
              )}
              {formData.confirmPassword && formData.password === formData.confirmPassword && !errors.confirmPassword && (
                <p className="mt-1 text-sm text-green-600 dark:text-green-400">✓ Passwords match</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-4 py-3 bg-primary text-primary-foreground rounded-md font-medium transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <a
                href="/login"
                className="text-primary hover:underline font-medium"
              >
                Sign in
              </a>
            </p>
          </div>
        </div>

        {/* Terms Notice */}
        <div className="mt-6 p-4 bg-muted/50 border border-border rounded-md">
          <p className="text-xs text-muted-foreground text-center">
            By creating an account, you agree to our{' '}
            <a href="/terms" className="text-primary hover:underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" className="text-primary hover:underline">
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  )
}
